/**
 * A router, in about a hundred lines.
 *
 * **Why not react-router.** Twelve flat routes, no nesting, no loaders, no data
 * layer. The library is excellent and would be 20KB of machinery for a `switch`
 * statement. More importantly it would not solve the one genuinely awkward
 * problem here, which is that this bundle runs in two places with different
 * ideas about what a URL is.
 *
 * **On the web, real paths.** Stripe redirects the browser to
 * `BILLING_SUCCESS_URL`, which defaults to `/billing/success` — a path, not a
 * fragment. Hash routing would mean Stripe returning to a URL the app cannot
 * read without a redirect dance. Real paths need the host to serve `index.html`
 * for unknown paths; Vite does this in dev, and the launch guide says so for
 * production.
 *
 * **Inside the desktop shell, the hash.** Tauri loads the page from the bundle
 * at `tauri://localhost`. `pushState` works, but a reload lands on a path no
 * bundle entry matches. The hash never leaves the document, so it always
 * survives. The desktop never receives a Stripe redirect anyway — checkout opens
 * in the system browser — so nothing is lost.
 *
 * The mode is decided once, at module load, from `isDesktop()`.
 */
import { useSyncExternalStore } from 'react';
import type { JSX, MouseEvent, ReactNode } from 'react';
import { isDesktop } from './runtime.js';

const HASH_MODE = isDesktop();

/**
 * Where the application is mounted on the web.
 *
 * `/` belongs to the marketing site, so the app lives under `/app`. Route
 * strings elsewhere in the codebase stay unprefixed — `/journal`, `/settings` —
 * and this is the single place the prefix is added on the way out and stripped
 * on the way in. Nothing else in the app knows it is not at the root.
 *
 * Empty inside the desktop shell. Tauri loads from the bundle root and routes
 * through the hash, so a path prefix there would point at nothing.
 */
export const BASE = HASH_MODE ? '' : ((import.meta.env?.VITE_APP_BASE as string | undefined) ?? '/app');

/** Strip the mount prefix, so the rest of the app sees `/journal` not `/app/journal`. */
function unbase(loc: string): string {
  if (!BASE) return loc;
  if (loc === BASE) return '/';
  return loc.startsWith(`${BASE}/`) ? loc.slice(BASE.length) : loc;
}

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

if (typeof window !== 'undefined') {
  // Registered once for the lifetime of the module rather than per subscriber,
  // so a component mounting does not add a duplicate window listener.
  window.addEventListener('popstate', emit);
  window.addEventListener('hashchange', emit);
}

/** The current location as the router sees it: path plus query, no origin. */
export function currentLocation(): string {
  if (typeof window === 'undefined') return '/';
  if (HASH_MODE) {
    const raw = window.location.hash.replace(/^#/, '');
    return raw || '/';
  }
  return unbase(window.location.pathname) + window.location.search;
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

export interface Route {
  /** Path only, no query string. Always starts with `/`, never ends with one. */
  readonly path: string;
  readonly query: URLSearchParams;
}

/**
 * Split a location into path and query.
 *
 * Exported for its tests. Routes are matched by exact string in `App.tsx`, so
 * `/settings/` arriving as a distinct value from `/settings` would render "not
 * found" for a link that looks correct — hence the trailing-slash trim, and
 * hence a test for it.
 */
export function parseLocation(loc: string): Route {
  const [rawPath = '/', rawQuery = ''] = loc.split('?');
  const path = rawPath.length > 1 ? rawPath.replace(/\/+$/, '') : rawPath;
  return { path: path || '/', query: new URLSearchParams(rawQuery) };
}

/** Re-renders the calling component whenever the location changes. */
export function useRoute(): Route {
  const loc = useSyncExternalStore(subscribe, currentLocation, () => '/');
  return parseLocation(loc);
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (typeof window === 'undefined') return;
  if (currentLocation() === to) return;
  const target = HASH_MODE ? to : `${BASE}${to === '/' ? '/' : to}`;

  if (HASH_MODE) {
    const next = `${window.location.pathname}${window.location.search}#${to}`;
    if (opts.replace) window.location.replace(next);
    else window.location.hash = to;
  } else if (opts.replace) {
    window.history.replaceState(null, '', target);
  } else {
    window.history.pushState(null, '', target);
  }

  // Scrolled through the document element rather than `window.scrollTo`, which
  // jsdom does not implement and which therefore fills every test run with
  // "Not implemented" noise for a call that does nothing there anyway.
  if (document.scrollingElement) document.scrollingElement.scrollTop = 0;

  // `hashchange` and `popstate` do not fire for programmatic navigation, so the
  // notification is explicit. Emitting twice in hash mode is harmless — the
  // store snapshot is a string, and an unchanged one does not re-render.
  emit();
}

/** The `href` a given route should carry, so links are real links in both modes. */
export function href(to: string): string {
  if (HASH_MODE) return `#${to}`;
  return `${BASE}${to === '/' ? '/' : to}`;
}

export interface LinkProps {
  readonly to: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly replace?: boolean;
  readonly onNavigate?: () => void;
  readonly 'aria-current'?: 'page' | undefined;
  /**
   * Tooltip text. Used by the collapsed navigation rail, where the icon is the
   * only visible content and the mouse user has nothing else to read.
   */
  readonly title?: string | undefined;
}

/**
 * An anchor that navigates in-app.
 *
 * It stays a real `<a href>` with a real destination so middle-click, ⌘-click
 * and "copy link address" all behave, and so a screen reader announces a link
 * rather than a mystery. Only an unmodified left click is intercepted.
 */
export function Link({
  to, children, className, replace, onNavigate, ...rest
}: LinkProps): JSX.Element {
  const onClick = (e: MouseEvent<HTMLAnchorElement>): void => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to, { replace });
    onNavigate?.();
  };

  return (
    <a href={href(to)} className={className} onClick={onClick} {...rest}>
      {children}
    </a>
  );
}
